<script setup>
import {ref} from 'vue'
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Select from '@/components/forms/Select.vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n()

// Country options use ISO codes as values — same conversion pattern as nationality/citizenship selects.
// Labels reuse the existing nationality translation keys to avoid duplication.
const countrySelectOptions = [
  {value: 'CZ', label: t('createUpdateClientModal.nationality_CZ')},
  {value: 'SK', label: t('createUpdateClientModal.nationality_SK')},
  {value: 'PL', label: t('createUpdateClientModal.nationality_PL')},
  {value: 'UA', label: t('createUpdateClientModal.nationality_UA')},
  {value: 'HU', label: t('createUpdateClientModal.nationality_HU')},
  {value: 'AT', label: t('createUpdateClientModal.nationality_AT')},
  {value: 'CH', label: t('createUpdateClientModal.nationality_CH')},
  {value: 'DE', label: t('createUpdateClientModal.nationality_DE')},
  {value: 'LI', label: t('createUpdateClientModal.nationality_LI')},
  {value: 'RS', label: t('createUpdateClientModal.nationality_RS')},
  // IMA-525: Kazachstán
  {value: 'KZ', label: t('createUpdateClientModal.nationality_KZ')},
];

const props = defineProps({
  address: {type: Object, required: false, default: {street: null, city: null, zip_code: null, country: null}},
});

const address = ref({...props.address});

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const closeModalWith = (value) => {
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', address.value);
    } else {
      emit('cancel');
    }
  }, 300);
}

function openModal() {
  isOpen.value = true
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ address.id ? t('saveClientAddressModal.adress_edit') : t('saveClientAddressModal.new_address') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="mb-3">
                  <label for="typeSelect" class="form-label">{{ t('saveClientAddressModal.address_type') }}</label>
                  <select v-model="address.type" id="typeSelect" class="form-select">
                    <option value="DOMICILE">{{ t('saveClientAddressModal.domicile') }}</option>
                    <option value="CORRESPONDENCE">{{ t('saveClientAddressModal.correspondence') }}</option>
                    <option value="OTHER">{{ t('saveClientAddressModal.other') }}</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label for="streetInput" class="form-label">{{ t('saveClientAddressModal.street') }}</label>
                  <input v-model="address.street" id="streetInput" class="form-control"/>
                </div>
                <div class="mb-3">
                  <label for="cityInput" class="form-label">{{ t('saveClientAddressModal.city') }}</label>
                  <input v-model="address.city" id="cityInput" class="form-control"/>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="zipCodeInput" class="form-label">{{ t('saveClientAddressModal.zip_code') }}</label>
                    <input v-model="address.zip_code" id="zipCodeInput" class="form-control" type="text"/></div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="countrySelect" class="form-label">{{ t('saveClientAddressModal.country') }}</label>
                    <!-- Country stored as ISO code (CZ, SK, …) — same value/label conversion as nationality select -->
                    <Select id="countrySelect" v-model="address.country" :options="countrySelectOptions" class="w-100"/>
                  </div>
                </div>
                <div class="mb-3">
                  <label for="noteInput" class="form-label">{{ t('saveClientAddressModal.note') }}</label>
                  <input v-model="address.note" id="noteInput" class="form-control"/>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('saveClientAddressModal.cancel') }}</button>
                <button class="btn btn-primary btn-sm" type="submit">{{ t('saveClientAddressModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
